import {
  ArrowTopRightOnSquareIcon,
  ArrowUturnLeftIcon,
  DocumentTextIcon,
  ListBulletIcon,
  PencilIcon,
  PhotoIcon,
  TrashIcon,
  VideoCameraIcon,
} from '@heroicons/react/16/solid';
import { forwardRef, useState } from 'react';
import { Button, Loading } from 'react-daisyui';
import { useTranslation } from 'react-i18next';
import Lightbox from 'yet-another-react-lightbox';
import Captions from 'yet-another-react-lightbox/plugins/captions';
import Download from 'yet-another-react-lightbox/plugins/download';
import Fullscreen from 'yet-another-react-lightbox/plugins/fullscreen';
import Thumbnails from 'yet-another-react-lightbox/plugins/thumbnails';
import Video from 'yet-another-react-lightbox/plugins/video';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import { useAnalytics } from 'src/hooks/analytics/useAnalytics.hook';
import { useBoardSlice } from 'src/pages/Pipe/PipeBoard.context';
import { eAlertType } from '../../../../../../../../providers/ToastProvider/Toast.enum.ts';
import { useToastContext } from '../../../../../../../../providers/ToastProvider/Toast.hook.ts';
import { useWorkspaceContext } from '../../../../../../../../providers/WorkspaceProvider/Workspace.context.ts';
import { ChatEnum, MediaType } from '../../../../../../Chat.enum.ts';
import { parseSlides } from '../../../../../../Chat.functions.ts';
import { useChatContext } from '../../../../../../Chat.hook.ts';
import { ChatService } from '../../../../../../Chat.service.ts';
import {
  tAttachment,
  tChatAttachment,
  tInteractiveType,
  tQuickReply,
} from '../../../../../../Chat.types.ts';
import { useMessageBarContext } from '../../../../MessageBar.hook.ts';
import { getFileMetaData } from '../../../Thumbnail/Thumbnail.functions.ts';
import { insertQuickReplyText } from '../../QuickAction.functions.ts';
import './QuickActionRow.styles.scss';

// Inline pill that identifies interactive quick replies by variant. Kept
// light-weight (single element, text + icon) so a long list of quick
// replies doesn't get visually noisy — pill only renders for the three
// outbound interactive variants, never for text-based replies.
function InteractiveTypePill({ type }: { type: tInteractiveType }) {
  const { t } = useTranslation(['chat/chat']);
  const Icon =
    type === 'button'
      ? ArrowUturnLeftIcon
      : type === 'cta_url'
        ? ArrowTopRightOnSquareIcon
        : ListBulletIcon;
  return (
    <span
      className={`QuickActionRow__TypePill QuickActionRow__TypePill--${type}`}
      title={t(`quickActions.interactivePill.${type}`)}
    >
      <Icon className="QuickActionRow__TypePill__Icon" />
      <span className="QuickActionRow__TypePill__Label">
        {t(`quickActions.interactivePill.${type}`)}
      </span>
    </span>
  );
}

const QuickActionRow = forwardRef<HTMLDivElement, tQuickReply>(
  function QuickActionRow(props: tQuickReply, ref) {
    const {
      selectedChat,
      quickReplies,
      setQuickReplies,
      fetchQuickReplies,
      textAreaRef,
    } = useChatContext();
    const { currentWorkspaceId, checkIfWorkspaceHasMoreThan90Days } =
      useWorkspaceContext();
    const {
      setSelectedAction,
      setChatAttachments,
      messageBarValue,
      setMessageBarValue,
      setInteractiveDraft,
      quickReplyTriggerRef,
    } = useMessageBarContext();
    const { addAlert } = useToastContext();
    const { trackEvent } = useAnalytics();

    const { currentFunnel } = useBoardSlice();

    const [loading, setLoading] = useState<boolean>(false);
    const [openLightbox, setOpenLightbox] = useState<boolean>(false);

    function onCloseLightbox() {
      setOpenLightbox(false);
    }

    async function deleteAndRemoveQuickAction() {
      try {
        if (!currentWorkspaceId || !selectedChat?.id) return;

        setLoading(true);

        await ChatService.deleteQuickReply(currentWorkspaceId, props.id);

        const updatedQuickReplies =
          quickReplies?.filter((quickAction) => quickAction.id !== props.id) ||
          null;
        setQuickReplies(updatedQuickReplies);
        fetchQuickReplies();

        addAlert({
          type: eAlertType.SUCCESS,
          message: `Respuesta rápida eliminada correctamente`,
        });
      } catch (error) {
        addAlert({
          type: eAlertType.ERROR,
          message: `No pudimos eliminar la respuesta rápida, intenta de nuevo ${error}`,
        });
      }
      setLoading(false);
    }

    const MediaIcon = ({ attachment }: { attachment: tAttachment }) =>
      ((attachment.mediaType === MediaType.IMAGE ||
        attachment.mediaType === MediaType.STICKER) && (
        <PhotoIcon className="w-5 text-primary" />
      )) ||
      (attachment.mediaType === MediaType.VIDEO && (
        <VideoCameraIcon className="w-5 text-primary" />
      )) ||
      // (file.mediaType === MediaType.AUDIO && (
      //   <audio src={file.url || undefined} />
      // )) ||
      (attachment.mediaType === MediaType.DOCUMENT && (
        <DocumentTextIcon className="w-8 text-primary" />
      )) ||
      null;

    function handleSelectQuickAction() {
      // Interactive quick replies take the seller straight into the composer
      // with a deep-cloned draft so edits here cannot mutate the list entry
      // (nested `sections[].rows[]` arrays would otherwise share references
      // with the quickReplies context).
      const interactive = props.content.interactive;
      const isOutboundInteractive =
        interactive !== undefined &&
        (interactive.type === 'button' ||
          interactive.type === 'cta_url' ||
          interactive.type === 'list');
      if (isOutboundInteractive) {
        setInteractiveDraft(structuredClone(interactive));
        setSelectedAction(ChatEnum.INTERACTIVE);
        return;
      }

      const attachments: tChatAttachment[] =
        props.content.attachments?.map((file) => {
          let fileName = file.filename || '';
          if (file.mediaType === MediaType.AUDIO) {
            fileName = file.url || '';
          }
          const metaData = getFileMetaData(fileName);

          const attachment = {
            type: metaData.mimeType,
            src: file.url || '',
            name: file.filename || '',
            size: 0,
            poster: '',
          };

          return attachment;
        }) || [];

      const newValue = insertQuickReplyText({
        text: messageBarValue,
        cursor: textAreaRef.current?.selectionStart ?? messageBarValue.length,
        triggerIndex: quickReplyTriggerRef.current,
        replyText: props.content.text?.trim() || '',
      });

      setMessageBarValue(newValue);
      setChatAttachments(attachments);
      setSelectedAction(ChatEnum.CHAT);
    }

    function trackSelectQuickActionEvent() {
      if (
        currentFunnel?.fromIndustryTemplate === true &&
        !checkIfWorkspaceHasMoreThan90Days()
      ) {
        trackEvent('front.funnels.quickActions.clicked_from_industry_template');
      }
    }

    const attachments = props?.content?.attachments || [];
    const attachmentsLength = attachments?.length;
    const hasAttachments = Boolean(attachmentsLength);

    // Only outbound interactive variants get a pill — inbound reply events
    // (`button_reply` / `list_reply`) never appear in the quick-replies
    // list because sellers can't author those as templates.
    const interactive = props.content.interactive;
    const interactiveVariant: tInteractiveType | null =
      interactive &&
      (interactive.type === 'button' ||
        interactive.type === 'cta_url' ||
        interactive.type === 'list')
        ? interactive.type
        : null;

    return (
      <div
        ref={ref}
        tabIndex={0}
        className={`QuickActionRow ${props.selectedIndex === props.index && 'bg-primary/10'}`}
        onClick={() => {
          handleSelectQuickAction();
          trackSelectQuickActionEvent();
        }}
      >
        <div className="QuickActionRow__Container">
          <div className="QuickActionRow__Container__Title">
            <p>
              <span className="font-bold font-sans">{`/${props.shortCommand}. `}</span>
              <span>{props.content?.text}</span>
            </p>
            {interactiveVariant ? (
              <InteractiveTypePill type={interactiveVariant} />
            ) : null}
          </div>

          <div className="QuickActionRow__Container__Action">
            <Button
              size="xs"
              color="ghost"
              shape="circle"
              onClick={(e) => {
                e.stopPropagation();
                // Interactive quick replies open directly in the composer
                // with a cloned draft (same pattern as row click). The text-
                // based CreateQuickAction form can't render the interactive
                // builder yet — that extension is a follow-up slice.
                const interactive = props.content.interactive;
                const isOutboundInteractive =
                  interactive !== undefined &&
                  (interactive.type === 'button' ||
                    interactive.type === 'cta_url' ||
                    interactive.type === 'list');
                if (isOutboundInteractive) {
                  setInteractiveDraft(structuredClone(interactive));
                  setSelectedAction(ChatEnum.INTERACTIVE);
                  return;
                }
                setSelectedAction(ChatEnum.CREATE_QUICK_ACTION);
                if (props.setQuickActionState) {
                  props.setQuickActionState({
                    id: props.id,
                    title: props.shortCommand,
                    isPublic: props.isPublic,
                    text: props.content?.text || '',
                    attachments: props.content.attachments || undefined,
                  });
                }
              }}
            >
              <PencilIcon className="QuickActionRow__Container__Action__Icon" />
            </Button>

            <Button
              size="xs"
              color="ghost"
              shape="circle"
              onClick={(e) => {
                e.stopPropagation();
                deleteAndRemoveQuickAction();
              }}
            >
              {loading ? (
                <Loading className="QuickActionRow__Container__Action__Icon" />
              ) : (
                <TrashIcon className="QuickActionRow__Container__Action__Icon" />
              )}
            </Button>
          </div>
        </div>

        {hasAttachments ? (
          <div className="flex gap-2 items-center justify-end">
            {attachments.slice(0, 3).map((file) => (
              <div
                className="flex gap-2 items-center border rounded-badge w-1/4 py-1 px-4 cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenLightbox(true);
                }}
              >
                <MediaIcon attachment={file} />
                <p className="truncate">
                  {file?.filename ||
                    (file?.mimeType
                      ? `${file.mimeType.split('/')?.[1]} File`
                      : 'File')}
                </p>
              </div>
            ))}

            {attachmentsLength > 3 ? (
              <div className="text-sm border rounded-full w-8 h-8 flex justify-center items-center">
                <p>{`+${attachmentsLength - 3}`}</p>
              </div>
            ) : null}

            {openLightbox && (
              <Lightbox
                plugins={[
                  Zoom,
                  Captions,
                  Thumbnails,
                  Download,
                  Fullscreen,
                  Video,
                ]}
                open={openLightbox}
                close={onCloseLightbox}
                slides={parseSlides(attachments)}
              />
            )}
          </div>
        ) : null}
      </div>
    );
  },
);

export default QuickActionRow;
